import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import type { ImageURISource, LayoutChangeEvent } from "react-native";
import { isWeb, styled } from "tamagui";
import { shadow } from "../../themes/token-shadow";
import { Avatar, type AvatarImageSource, type AvatarProps } from "../Avatar/Avatar";
import { SelectionCard } from "../Card/SelectionCard";
import { DecorativeTrailing } from "../DecorativeTrailing";
import { Image } from "../Image";
import { RowLayout } from "../Row/RowLayout";
import { YStack } from "../Stacks";
import { Body1, Body2, Subtitle1 } from "../Typography";
import { useSelectionItem } from "./SelectionContext";
import { renderIndicator } from "./SelectionRenderers";
type SelectionRowCommonProps = {
  value: string;
  label: string;
  leading?: ({
    type: "avatar";
  } & Omit<AvatarProps, "size" | "shape">) | {
    type: "image";
    source: AvatarImageSource;
  };
  /** Decorative trailing element (badge or pip). Ignored when the group has showSelector enabled. */
  trailing?: DecorativeTrailing;
  disabled?: boolean;
  /** Taller tap target with a centred label and a selected shadow: the row counterpart of `SelectionTile`'s `prominent`. */
  prominent?: boolean;
  testID?: string;
  children?: ReactNode;
};
const COLLAPSIBLE_TRANSITION_MS = 200;
const COLLAPSIBLE_SETTLE_BUFFER_MS = 20;

// `display: contents` keeps the wrapper out of the layout box model.
const webRefWrapperStyle = {
  display: "contents"
} as const;
type SelectionRowProps = SelectionRowCommonProps & ({
  /** Titled: prominent Subtitle1 label with optional Body2 description underneath. */
  variant?: "titled";
  description?: string;
} | {
  /** Simple: single Body1 text block that wraps. No description. */
  variant: "simple";
  description?: never;
});
const Collapsible = ({
  expanded,
  children
}: {
  expanded: boolean;
  children: ReactNode;
}) => {
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [animating, setAnimating] = useState(false);
  const [prevExpanded, setPrevExpanded] = useState(expanded);
  const onLayout = useCallback((e: LayoutChangeEvent) => {
    const measured = e.nativeEvent.layout.height;
    if (measured > 0) {
      setContentHeight(prev => prev === measured ? prev : measured);
    }
  }, []);

  // overflow:visible after settle so inner shadows aren't clipped.
  if (prevExpanded !== expanded) {
    setPrevExpanded(expanded);
    if (contentHeight !== null) {
      setAnimating(true);
    }
  }
  useEffect(() => {
    if (!animating) {
      return undefined;
    }
    const handle = setTimeout(() => setAnimating(false), COLLAPSIBLE_TRANSITION_MS + COLLAPSIBLE_SETTLE_BUFFER_MS);
    return () => clearTimeout(handle);
  }, [animating]);
  const resolvedHeight = expanded ? contentHeight ?? undefined : 0;
  const clip = !expanded || animating;
  return <YStack overflow={clip ? "hidden" : "visible"} height={resolvedHeight} opacity={expanded ? 1 : 0} transition={contentHeight !== null ? `${COLLAPSIBLE_TRANSITION_MS}ms easeOut` : undefined} animateOnly={["height", "opacity"]} data-sentry-element="YStack" data-sentry-component="Collapsible" data-sentry-source-file="SelectionRow.tsx">
      <YStack onLayout={onLayout} paddingTop="$sp2.5" position="absolute" top={0} left={0} right={0} data-sentry-element="YStack" data-sentry-source-file="SelectionRow.tsx">
        {children}
      </YStack>
    </YStack>;
};
const LabelText = styled(Subtitle1, {
  name: "LabelText",
  color: "$textDefault",
  flex: 1,
  variants: {
    disabled: {
      true: {
        color: "$textSubtle"
      }
    }
  }
} as const);
const DescriptionText = styled(Body2, {
  name: "DescriptionText",
  color: "$textSubtle",
  variants: {
    disabled: {
      true: {
        color: "$textMuted"
      }
    }
  }
} as const);
const SimpleText = styled(Body1, {
  name: "SimpleText",
  color: "$textDefault",
  flex: 1,
  variants: {
    disabled: {
      true: {
        color: "$textSubtle"
      }
    }
  }
} as const);
const LeadingImage = styled(Image, {
  name: "SelectionRowLeadingImage",
  width: "$si6",
  height: "$si6",
  borderRadius: "$br2",
  objectFit: "contain"
} as const);
const PrimaryTrailing = ({
  showSelector,
  trailing,
  mode,
  selected,
  disabled
}: {
  showSelector: boolean;
  trailing?: DecorativeTrailing;
  mode: "radio" | "checkbox" | "switch";
  selected: boolean;
  disabled: boolean;
}) => {
  if (showSelector) {
    return <YStack pointerEvents="none" aria-hidden={true}>
        {renderIndicator({
        mode,
        selected,
        disabled
      })}
      </YStack>;
  }
  if (trailing) {
    return <YStack pointerEvents="none" aria-hidden={true}>
        <DecorativeTrailing {...trailing} />
      </YStack>;
  }
  return null;
};
const RowLeading = ({
  leading
}: {
  leading?: SelectionRowProps["leading"];
}) => {
  if (!leading) {
    return null;
  }
  if (leading.type === "avatar") {
    const {
      type: _type,
      ...avatarProps
    } = leading;

    // Preserve the full fallback ladder: if `image` is present but fails to
    // load, Avatar falls back to `initials` then `icon`. We must therefore
    // forward initials/icon alongside image instead of stripping them.
    if (avatarProps.image != null) {
      return <Avatar size="medium" shape="square" image={avatarProps.image} initials={avatarProps.initials} icon={avatarProps.icon} />;
    }
    if (avatarProps.initials != null) {
      return <Avatar size="medium" shape="square" initials={avatarProps.initials} icon={avatarProps.icon} />;
    }
    if (avatarProps.icon != null) {
      return <Avatar size="medium" shape="square" icon={avatarProps.icon} />;
    }
    return null;
  }
  const nativeImageSource: ImageURISource | number = typeof leading.source === "string" ? {
    uri: leading.source
  } : leading.source;
  const webImageSource = typeof leading.source === "string" ? leading.source : undefined;
  if (isWeb) {
    return webImageSource != null ? <LeadingImage src={webImageSource} /> : null;
  }
  return <LeadingImage
  // Tamagui types `src` as string; native also accepts require() numbers
  // and URI objects (see ResponsiveImage.native).
  // @ts-expect-error — Tamagui's `src` typing is narrower than its runtime contract.
  src={nativeImageSource} data-sentry-element="LeadingImage" data-sentry-component="RowLeading" data-sentry-source-file="SelectionRow.tsx" />;
};

/** Shared with FieldShell's tall size so fields and rows on one screen match. */
export const PROMINENT_ROW_MIN_HEIGHT = "$si9";
export const SelectionRow = ({
  value,
  label,
  leading,
  trailing,
  disabled: disabledProp,
  prominent = false,
  testID,
  children,
  ...rest
}: SelectionRowProps) => {
  const variant = rest.variant ?? "titled";
  const labelAlign = prominent ? "center" : undefined;
  const description = variant === "titled" ? rest.description : undefined;
  const {
    group,
    disabled,
    selected,
    onPress
  } = useSelectionItem(value, disabledProp);
  const hasCollapsibleContent = children != null;
  // Inner content owns interaction once expanded — outer card goes static
  // (fixes button-in-button HTML and hover/press bleed onto inner controls).
  const isStaticSelected = selected && hasCollapsibleContent;

  // Shift focus into expanded content when the outer button disappears.
  // Web-only — native uses a different focus model.
  const expandedContentRef = useRef<HTMLDivElement>(null);
  const wasStaticSelected = useRef(false);
  useEffect(() => {
    if (isStaticSelected && !wasStaticSelected.current && expandedContentRef.current) {
      const focusable = expandedContentRef.current.querySelector<HTMLElement>('button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])');
      focusable?.focus();
    }
    wasStaticSelected.current = isStaticSelected;
  }, [isStaticSelected]);
  const trailingSlot = <PrimaryTrailing showSelector={group.showSelector} trailing={trailing} mode={group.mode} selected={selected} disabled={disabled} />;
  const primary = variant === "simple" ? <SimpleText disabled={disabled} textAlign={labelAlign}>
        {label}
      </SimpleText> : <LabelText disabled={disabled} textAlign={labelAlign}>
        {label}
      </LabelText>;
  const secondary = description ? <DescriptionText disabled={disabled}>{description}</DescriptionText> : undefined;
  return <SelectionCard kind={isStaticSelected ? "static" : "button"} selected={selected} tone={group.tone} disabled={disabled} role={group.mode} aria-label={label} aria-checked={selected} {...children ? {
    "aria-expanded": selected
  } : {}} {...testID !== undefined ? {
    testID
  } : {}} onPress={onPress} {...prominent ? {
    minHeight: PROMINENT_ROW_MIN_HEIGHT,
    justifyContent: "center",
    ...(selected ? shadow.medium : {})
  } : {}} data-sentry-element="SelectionCard" data-sentry-component="SelectionRow" data-sentry-source-file="SelectionRow.tsx">
      <RowLayout leading={leading != null ? <RowLeading leading={leading} /> : undefined} primary={primary} primaryTrailing={trailingSlot} secondary={secondary} data-sentry-element="RowLayout" data-sentry-source-file="SelectionRow.tsx" />

      {children ? <Collapsible expanded={selected}>
          {isWeb ? <div ref={expandedContentRef} style={webRefWrapperStyle}>
              {children}
            </div> : children}
        </Collapsible> : null}
    </SelectionCard>;
};
export type { SelectionRowProps };